import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import { useLanguage } from 'assets/languages';
import { queries } from 'api/api.constants';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import { CAMPAIGN_DTO_ENUMS } from 'api/repositories/campaign/campaign.constants';
import { GuidelineRulesDto } from 'api/repositories/guidelineRules/guidelineRules.types';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import {
  BEAUTY_OUTPUT_GROUP_NAMES,
  GUIDELINE_RULE_ARRAY_GROUPS,
  GUIDELINE_RULE_TAG_GROUPS,
} from 'pages/campaigns/campaignForm/campaignForm.constants';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import {
  GuidelineRuleFormField,
  OnSuccessStoreOrUpdateTemporaryCampaignCallbackResponse,
} from 'pages/campaigns/campaignForm/campaignForm.types';
import { getRecommendedBeautyOutputGroupNames } from './campaignFormStepTwo.constants';

interface CampaignFormStepTwoContextProps {
  baseGuidelineRules: GuidelineRulesDto;
  isFormValid: boolean;
  removeTagFixes: (tagText: string) => string;
  validateMultipleUrlsInText: (text: string) => boolean;
  validateDocumentOrSnsPlatformsInText: (text: string) => void;
  onSuccessStoreOrUpdateTemporaryCampaignCallback: (
    response: OnSuccessStoreOrUpdateTemporaryCampaignCallbackResponse,
  ) => void;
}

const initCampaignFormStepTwoContextData: CampaignFormStepTwoContextProps = {
  baseGuidelineRules: [],
  isFormValid: false,
  removeTagFixes: () => '',
  validateMultipleUrlsInText: () => false,
  validateDocumentOrSnsPlatformsInText: () => {},
  onSuccessStoreOrUpdateTemporaryCampaignCallback: () => {},
};

export const CampaignFormStepTwoContext = createContext<CampaignFormStepTwoContextProps>(
  initCampaignFormStepTwoContextData,
);

export const CampaignFormStepTwoContextProvider = ({ children }: { children: ReactNode }) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const {
    modal,
    auth,
    brand,
    campaignFormMethods,
    guidelineRuleFormMethods,
    getGuidelineRulesParams,
    setShowLoader,
    setStep,
    isInitializedGuidelineRules,
    setIsInitializedGuidelineRules,
  } = useContext(CampaignFormContext);

  const outputTypeFl = useWatch({
    control: campaignFormMethods?.control,
    name: 'outputTypeFl',
  });

  const [atTags, hashTags] = useWatch({
    control: guidelineRuleFormMethods?.control,
    name: [GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.AT_TAGS, GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.HASH_TAGS],
  });

  const isFormValid = useMemo(() => {
    if (
      // 브랜드 인스타그램 공식 계정이 없는 경우
      (atTags?.value as string[])?.filter(Boolean)?.length <= 0 ||
      // 해시태그가 없는 경우
      (hashTags?.value as string[])?.filter(Boolean)?.length <= 0
    ) {
      return false;
    } else {
      return true;
    }
  }, [atTags, hashTags]);

  /**
   * 태그 문자를 제거하는 함수
   * @example
   * removeTagFixes("@tagText") => "tag"
   * removeTagFixes("#tagText") => "tag"
   */
  const removeTagFixes = useCallback((tagText: string) => {
    return tagText ? tagText.replace(/[@#\s]/g, '') : '';
  }, []);

  // 기본 Guideline Rule 가져오기
  const { data: baseGuidelineRules } = useQuery({
    ...queries.guidelineRules.getBaseGuidelineRules({
      brandToken: auth.token,
      brandBusinessType: brand?.businessType,
      brandCategoryId: brand?.categoryId,
      userTypeFl: GUIDELINE_RULE_DTO_ENUMS.USER_TYPE_FL.CAMPAIGN_NORMAL,
      outputType: outputTypeFl === CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO ? 1 : 2,
    }),
    enabled: !!auth.token && !!brand?.businessType,
    select: (data) => data.data.rules,
    staleTime: 1000 * 60 * 5, // 5분
    gcTime: 1000 * 60 * 5, // 5분
  });

  // 가이드라인 조회
  const { data: guidelineRules } = useQuery({
    ...queries.guidelineRules.getGuidelineRules(getGuidelineRulesParams),
    enabled: !!auth.token && !isInitializedGuidelineRules,
    select: (data) => data.data.rules,
  });

  const defaultGuidelineRuleFormValues = useMemo(() => {
    const atTag = brand?.atTag?.split(/\s+/).map((tag) => removeTagFixes(tag));

    const guidelineRules = {
      [GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.AT_TAGS]: {
        value: atTag || [''],
      },
      [GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.HASH_TAGS]: { value: [''] },
    };

    switch (brand.categoryId) {
      case BRAND_DTO_ENUMS.CATEGORY_ID.BEAUTY: {
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PREFERRED_VIDEO_TYPE] = {
          value: null,
        };
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.VIDEO_DURATION] = {
          value: GUIDELINE_RULE_DTO_ENUMS.VIDEO_DURATION_OPTIONS.THIRTY,
        };
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.SUBTITLE_MIDDLE] = [{ value: null }];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.GUIDE_IMAGE] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.BRAND_LOGO] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.EMBLEM_OLIVEYOUNG] = [];

        /* @note 뷰티 필수 금지사항 (유저 인풋은 필요 없으나, 필수 검수 프로세스 및 UI 표기용으로 생성) */
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS_BEAUTY] =
          baseGuidelineRules
            ?.filter((baseRule) => baseRule.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS_BEAUTY)
            ?.map((baseRule) => {
              return { order: baseRule.order, value: baseRule.value };
            }) ?? [];

        /* @note 뷰티 필수 클린본 요청 (유저 인풋은 필요 없으나, 필수 검수 프로세스 및 UI 표기용으로 생성) */
        if (
          baseGuidelineRules?.find((baseRule) => {
            return baseRule.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.ATTACHMENT_RAW_VIDEO;
          }) != null
        ) {
          guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.ATTACHMENT_RAW_VIDEO] = { value: null };
        }
        break;
      }
      default: {
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_FULL] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_HALF] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PHOTO_MODEL_CLOSEUP] = [];
        guidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.ALLOW_ADVERTISEMENT_TAG] = {
          value: GUIDELINE_RULE_DTO_ENUMS.ALLOW_ADVERTISEMENT_TAG_OPTIONS.ALLOW,
        };
        break;
      }
    }

    return guidelineRules;
  }, [outputTypeFl, brand, baseGuidelineRules]);

  // URL이 2개 이상인지 확인하는 함수
  const validateMultipleUrlsInText = useCallback((text: string) => {
    const httpCount = (text?.match(/http/gi) || []).length;

    if (httpCount >= 2) {
      modal.showModal({
        hideCloseButton: true,
        title: languageTexts.create_reels_form_warning_multiple_url,
        text: languageTexts.create_reels_form_warning_multiple_url_desc,
      });

      return false;
    }

    return true;
  }, []);

  // 문서 플랫폼 문자가 포함되어 있는지 확인하는 함수
  const validateDocumentPlatformsInText = useCallback((text: string) => {
    const hasDocPlatform = ['notion', 'google', 'dropbox'].some((platform) => text?.toLowerCase()?.includes(platform));

    if (hasDocPlatform) {
      modal.showModal({
        hideCloseButton: true,
        title: languageTexts.create_reels_form_warning_document,
        text: languageTexts.create_reels_form_warning_document_desc,
      });

      return false;
    }

    return true;
  }, []);

  // SNS 플랫폼 문자가 포함되어 있는지 확인하는 함수
  const validateSnsPlatformsInText = useCallback((text: string) => {
    const hasSnsPlatform = ['instagram', 'youtube', 'tiktok'].some((platform) =>
      text?.toLowerCase()?.includes(platform),
    );

    if (hasSnsPlatform) {
      modal.showModal({
        hideCloseButton: true,
        title: languageTexts.create_reels_form_warning_sns_injection,
        text: languageTexts.create_reels_form_warning_sns_injection_desc,
      });

      return false;
    }

    return true;
  }, []);

  const validateDocumentOrSnsPlatformsInText = useCallback((text: string) => {
    const hasDocPlatform = validateDocumentPlatformsInText(text);

    if (hasDocPlatform) {
      validateSnsPlatformsInText(text);
    }
  }, []);

  const initializeGuidelineRulesFormData = useCallback(
    (guidelineRules: GuidelineRulesDto) => {
      // FILTER NOT EXISTING ON BASE GUIDELINE RULES
      if (baseGuidelineRules != null && guidelineRules != null) {
        guidelineRules = guidelineRules.filter((guidelineRule) => {
          return baseGuidelineRules.some((baseGuidelineRule) => {
            return (
              guidelineRule.groupName === baseGuidelineRule.groupName &&
              (guidelineRule.value === baseGuidelineRule.value || baseGuidelineRule.value == null)
            );
          });
        });
      }

      let parsedLoadedGuidelineRules: Record<string, GuidelineRuleFormField | GuidelineRuleFormField[]> = {};
      let hasLoadedBeautyOutputGroup = false;

      guidelineRules?.forEach(({ groupName, id, order, value }) => {
        const rule: GuidelineRuleFormField = { id, order, value };

        // 뷰티 필수 장면 중 권장 항목이 있는지 체크
        if (
          brand.isCategoryBeauty &&
          !hasLoadedBeautyOutputGroup &&
          BEAUTY_OUTPUT_GROUP_NAMES.some((beautyOutputGroupName) => beautyOutputGroupName === groupName)
        ) {
          hasLoadedBeautyOutputGroup = true;
        }

        if (GUIDELINE_RULE_ARRAY_GROUPS.some((arrayGroupName) => arrayGroupName === groupName)) {
          // 배열 타입인 경우
          if (!parsedLoadedGuidelineRules[groupName]) {
            parsedLoadedGuidelineRules[groupName] = [];
          }

          (parsedLoadedGuidelineRules[groupName] as GuidelineRuleFormField[]).push(rule);
        } else if (GUIDELINE_RULE_TAG_GROUPS.some((tagGroupName) => tagGroupName === groupName)) {
          // 태그 타입인 경우
          rule['value'] = (value as string)?.split(/\s+/).map((tag) => removeTagFixes(tag));
          parsedLoadedGuidelineRules[groupName] = rule;
        } else {
          parsedLoadedGuidelineRules[groupName] = rule;
        }
      });

      // 데이터 파싱 후 뷰티 필수 장면 중 권장 항목이 없으면 초기값 설정
      if (brand.isCategoryBeauty && !hasLoadedBeautyOutputGroup) {
        // 권장 항목 초기값 설정
        getRecommendedBeautyOutputGroupNames().forEach((recommendedGroupName) => {
          parsedLoadedGuidelineRules[recommendedGroupName] = [{ value: '' }];
        });
      }

      // 올리브영 선호하는 영상 유형이 아닐 경우, 올리브영 엠블렘을 제외
      let hasOliveyoungPreferredVideoType = false;
      const preferredVideoType = parsedLoadedGuidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PREFERRED_VIDEO_TYPE] as GuidelineRuleFormField | undefined;
      if (preferredVideoType?.value) {
        hasOliveyoungPreferredVideoType = ([
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_PRODUCT_ONLY,
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_VISITING,
        ] as string[]).includes(preferredVideoType.value as string);
      }
      if (!hasOliveyoungPreferredVideoType) {
        parsedLoadedGuidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.EMBLEM_OLIVEYOUNG] = [];
      }

      // 뷰티 필수 가이드라인 덮어쓰기 보정
      if (brand.isCategoryBeauty) {
        const newProhibitionsBeauty = [];
        const parsedValue_rule_map = {};
        for (let rule of (parsedLoadedGuidelineRules[
          GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS_BEAUTY
        ] as GuidelineRuleFormField[]) ?? []) {
          parsedValue_rule_map[rule.value as string] = rule;
        }

        for (let baseRule of (defaultGuidelineRuleFormValues[
          GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS_BEAUTY
        ] as GuidelineRuleFormField[]) ?? []) {
          if (parsedValue_rule_map[baseRule.value as string]) {
            newProhibitionsBeauty.push({
              ...parsedValue_rule_map[baseRule.value as string],
              order: baseRule.order,
            });
          } else {
            newProhibitionsBeauty.push(baseRule);
          }
        }
        parsedLoadedGuidelineRules[GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS_BEAUTY] = newProhibitionsBeauty;
      }

      guidelineRuleFormMethods.reset({
        ...defaultGuidelineRuleFormValues,
        ...parsedLoadedGuidelineRules,
      });
      setIsInitializedGuidelineRules(true);
    },
    [baseGuidelineRules, defaultGuidelineRuleFormValues],
  );

  const onSuccessStoreOrUpdateTemporaryCampaignCallback = useCallback((response) => {
    // Store 성공 후 업데이트된 가이드라인 정보로 데이터 reset
    initializeGuidelineRulesFormData(response.guidelineRules);
    setStep((prevStep) => prevStep + 1);
  }, [initializeGuidelineRulesFormData]);

  // 가이드라인 관련 데이터 Fetching 완료 후 form 초기화
  useEffect(() => {
    if (!isInitializedGuidelineRules) {
      if (!!guidelineRules) {
        initializeGuidelineRulesFormData(guidelineRules);
        setShowLoader(false);
      } else {
        setShowLoader(true);
      }
    }
  }, [isInitializedGuidelineRules, guidelineRules]);

  return (
    <>
      <CampaignFormStepTwoContext.Provider
        value={{
          baseGuidelineRules,
          isFormValid,
          removeTagFixes,
          validateMultipleUrlsInText,
          validateDocumentOrSnsPlatformsInText,
          onSuccessStoreOrUpdateTemporaryCampaignCallback,
        }}
      >
        {isInitializedGuidelineRules && children}
      </CampaignFormStepTwoContext.Provider>
    </>
  );
};
